import { useEffect, useState } from "preact/hooks";
import {
  shareRecording,
  downloadRecording,
  type Recording,
  type TakeSummary,
} from "./capture";
import { message, takes, takesCount, currentTake } from "./state";
import { formatTime } from "./script";
import { getTake, deleteTake } from "./storage";
import { Icon } from "./icons";

export function TakeCard({
  take,
  recording,
}: {
  take: TakeSummary;
  recording?: Recording;
}) {
  const [reviewOpen, setReviewOpen] = useState(!!recording);
  const [loaded, setLoaded] = useState<Recording | null>(null);
  const [url, setUrl] = useState("");
  useEffect(
    function () {
      if (!reviewOpen) return;
      let cancelled = false;
      let objectUrl = "";
      async function load() {
        try {
          const recent = currentTake.peek();
          const available =
            recording || (recent?.id === take.id ? recent : null);
          const video = available || (await getTake(take.id));
          if (cancelled) return;
          objectUrl = URL.createObjectURL(video.blob);
          setLoaded(video);
          setUrl(objectUrl);
        } catch {
          if (!cancelled) {
            message.value = "Could not open this take. Please try again.";
            setReviewOpen(false);
          }
        }
      }
      void load();
      return function () {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    },
    [take.id, recording, reviewOpen],
  );
  return (
    <article class="take-card">
      <div class="take-heading">
        <div>
          <h3>{take.title || "Untitled take"}</h3>
          <small>
            {formatTime(take.duration)} ·{" "}
            {new Intl.DateTimeFormat(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(take.created)}
          </small>
        </div>
        <span class="take-check">
          <Icon name="check" />
        </span>
      </div>
      {loaded && (
        <video
          src={url}
          controls
          playsInline
          preload="metadata"
          aria-label={`Review ${take.title}`}
        />
      )}
      <button
        class="outline-button"
        onClick={function () {
          setLoaded(null);
          setUrl("");
          setReviewOpen(!reviewOpen);
        }}
      >
        {reviewOpen ? "Close review" : "Review / export video"}
      </button>
      {reviewOpen && !loaded && <p>Loading video…</p>}
      {loaded && (
        <button
          class="primary save-video"
          onClick={async function () {
            try {
              const result = await shareRecording(loaded);
              message.value =
                result === "shared"
                  ? "Video handed to the share sheet. Choose Save Video or your preferred destination."
                  : "Download requested. Open the file to save it to Photos.";
            } catch (error) {
              if (!(
                error instanceof DOMException && error.name === "AbortError"
              ))
                message.value =
                  "Could not share the video. Use Download original instead.";
            }
          }}
        >
          <Icon name="download" /> Save / share video
        </button>
      )}
      <div class="take-actions">
        {loaded && (
          <button
            class="text-button"
            onClick={function () {
              downloadRecording(loaded);
            }}
          >
            Download original
          </button>
        )}
        <button
          class="text-button"
          onClick={async function () {
            if (
              !confirm(
                "Delete this take from this device? Download it first if you want to keep it.",
              )
            )
              return;
            try {
              await deleteTake(take.id);
              takesCount.value--;
              takes.value = takes.value.filter(function (item) {
                return item.id !== take.id;
              });
              if (currentTake.value?.id === take.id) currentTake.value = null;
            } catch {
              message.value = "Could not delete this take. Please try again.";
            }
          }}
        >
          Delete take
        </button>
      </div>
      <p class="save-note">
        On supported phones, choose “Save Video” in the share sheet to add it to
        Photos. Your original video is also kept in My takes on this device.
      </p>
    </article>
  );
}
